/**
 * Training List Page
 * List and manage trainings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { trainingService } from '../../services/trainingService';
import {
  Training,
  TrainingFilters,
  TrainingStatus,
  TrainingStatusLabels,
  TrainingStatusColors,
  TrainingNRTypeLabels,
  TrainingCategoryLabels,
} from '../../types/training';
import { TrainingFiltersComponent, TrainingFiltersData } from './components/TrainingFilters';
import { TrainingValidityStatusChip } from './components/TrainingValidityStatusChip';

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const baseColumns: DataTableColumn<Training>[] = [
  {
    id: 'employee',
    label: 'Funcionário',
    primary: true,
    render: (training) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {training.employeeName ?? '-'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {training.companyName ?? '-'}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'name',
    label: 'Treinamento',
    render: (training) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {training.name}
        </Typography>
        {training.workload && (
          <Typography variant="caption" color="text.secondary">
            {training.workload}h
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'type',
    label: 'Tipo',
    render: (training) => (
      <Chip
        label={training.type ? TrainingNRTypeLabels[training.type] : '-'}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    id: 'category',
    label: 'Categoria',
    render: (training) => (
      <Chip
        label={TrainingCategoryLabels[training.category]}
        size="small"
        color={training.category === 'INITIAL' ? 'primary' : training.category === 'PERIODIC' ? 'secondary' : 'default'}
        variant="outlined"
      />
    ),
  },
  {
    id: 'completedAt',
    label: 'Realização',
    align: 'center',
    render: (training) => <Typography variant="body2">{formatDate(training.completedAt)}</Typography>,
  },
  {
    id: 'expiresAt',
    label: 'Validade',
    align: 'center',
    render: (training) => <Typography variant="body2">{formatDate(training.expiresAt)}</Typography>,
  },
  {
    id: 'validityStatus',
    label: 'Vigência',
    align: 'center',
    render: (training) => (
      <TrainingValidityStatusChip
        status={training.validityStatus}
        daysUntilExpiry={training.daysUntilExpiry}
      />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (training) => (
      <Chip
        label={TrainingStatusLabels[training.status]}
        color={TrainingStatusColors[training.status]}
        size="small"
      />
    ),
  },
];

/** Treinamento cancelado aparece esmaecido, como na tabela antiga. */
const columns: DataTableColumn<Training>[] = baseColumns.map((column) => ({
  ...column,
  render: (training) => (
    <Box sx={{ opacity: training.status === TrainingStatus.CANCELLED ? 0.6 : 1 }}>
      {column.render(training)}
    </Box>
  ),
}));

export const TrainingListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/trainings/:id/edit', pode('training.update'), 'Editar');
  const excluir = useAcao('training.delete', 'Excluir');
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<TrainingFilters>({});

  const loadTrainings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await trainingService.getTrainings(params);
      setTrainings(response.trainings);
      setTotal(response.total);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar treinamentos';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  const handleFilter = useCallback((data: TrainingFiltersData) => {
    const filters: TrainingFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.type) filters.type = data.type as TrainingFilters['type'];
    if (data.category) filters.category = data.category as TrainingFilters['category'];
    if (data.validityStatus) filters.validityStatus = data.validityStatus as TrainingFilters['validityStatus'];
    if (data.status) filters.status = data.status as TrainingFilters['status'];

    setApiFilters(filters);
    setPage(0);
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este treinamento?')) {
      return;
    }

    try {
      await trainingService.deleteTraining(id);
      loadTrainings();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir treinamento';
      setError(errorMessage);
    }
  };

  const handleEdit = (training: Training) => {
    navigate(`/trainings/${training.id}/edit`);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Treinamentos' },
      ]}
      title="Treinamentos"
      subtitle="Gerencie os treinamentos e capacitações dos funcionários"
      actions={
        <>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<WarningIcon />}
            onClick={() => handleFilter({ searchTerm: '', type: '', category: '', validityStatus: 'WARNING', status: '' })}
          >
            Expirando
          </Button>
          <RouteActionButton
            para="/trainings/new"
            exige={pode('training.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Treinamento
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TrainingFiltersComponent onFilter={handleFilter} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={trainings}
        getRowId={(training) => training.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum treinamento encontrado',
          description:
            'Cada treinamento registra a realização de um tipo de treinamento e os funcionários que participaram dele.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (training) => handleDelete(training.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => {
            setRowsPerPage(n);
            setPage(0);
          },
        }}
      />
    </PageShell>
  );
};

export default TrainingListPage;
